import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, Modal, Radio, Select, Space, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import type { Quotation } from '@/services/quotation.service';
import {
  QUOTATION_REVISION_TYPE_OPTIONS,
  displayQuotationRevisionLabel,
} from '@/utils/quotationRevisionPolicy';

const REVISION_TYPE_HELP: Record<string, string> = {
  COMMERCIAL_ONLY: 'Price, discount or terms change without changing cost basis.',
  ESTIMATE_CHANGE: 'Use a newer approved Estimate revision as the cost source.',
  SCOPE_CHANGE: 'Scope, quantity or BOQ changes.',
  QUANTITY_CHANGE: 'Quantity adjustments without full scope change.',
  CUSTOMER_REQUEST: 'Change driven by explicit customer request.',
  TERMS_CHANGE: 'Payment, delivery or validity terms change.',
  NEGOTIATION: 'Negotiation round — commercial position update.',
  VALIDITY_EXTENSION: 'Extend validity without material commercial change.',
  INTERNAL_CORRECTION: 'Formal correction without material commercial impact.',
  OTHER: 'Other — describe in the reason field.',
};
const { Text } = Typography;
const { TextArea } = Input;

type EstimateSource = 'keep' | 'latest_approved' | 'selected';

type EstimationFamilyRow = {
  id: string;
  estimation_number?: string;
  revision_number?: number;
  status?: string;
  is_latest_revision?: boolean;
};

export type CreateQuotationRevisionValues = {
  revision_reason: string;
  revision_type: string;
  revision_notes?: string;
  estimate_source: EstimateSource;
  selected_estimation_id?: string;
};

type Props = {
  open: boolean;
  quotation: Quotation | null;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateQuotationRevisionValues) => void | Promise<void>;
};

function unwrapList<T>(res: unknown): T[] {
  const data = (res as { data?: unknown })?.data ?? res;
  const inner = (data as { data?: unknown })?.data ?? data;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

export const CreateQuotationRevisionModal: React.FC<Props> = ({
  open,
  quotation,
  loading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<CreateQuotationRevisionValues>();
  const [estimateSource, setEstimateSource] = useState<EstimateSource>('keep');
  const estimationId = String(quotation?.estimation_id || '').trim();

  const { data: estimationFamily = [], isFetching: familyLoading } = useQuery({
    queryKey: ['crm-estimation-revision-family', estimationId],
    enabled: open && !!estimationId,
    queryFn: async () => {
      const res = await api.get(`/crm/estimations/${estimationId}/revision-family`);
      return unwrapList<EstimationFamilyRow>(res);
    },
  });

  useEffect(() => {
    if (!open || !quotation) return;
    form.setFieldsValue({
      revision_type: 'COMMERCIAL_ONLY',
      revision_reason: '',
      revision_notes: '',
      estimate_source: 'keep',
      selected_estimation_id: estimationId || undefined,
    });
    setEstimateSource('keep');
  }, [open, quotation, form, estimationId]);

  const currentEstLabel = useMemo(() => {
    const current = estimationFamily.find((e) => e.id === estimationId);
    if (!current && !estimationId) return 'No linked estimation (commercial-only)';
    if (!current) return `Estimation ${estimationId.slice(0, 8)}…`;
    return `${current.estimation_number || 'EST'} · Rev ${current.revision_number ?? '—'} (${current.status || '—'})`;
  }, [estimationFamily, estimationId]);

  const latestApproved = useMemo(
    () => estimationFamily.find((e) => String(e.status || '').toLowerCase() === 'approved' && e.is_latest_revision),
    [estimationFamily]
  );

  const selectedRevisionType = Form.useWatch('revision_type', form);
  const typeHelp = selectedRevisionType ? REVISION_TYPE_HELP[selectedRevisionType] : undefined;

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({
      ...values,
      estimate_source: estimateSource,
      selected_estimation_id:
        estimateSource === 'selected' ? values.selected_estimation_id : undefined,
    });
  };

  return (
    <Modal
      title={`Create Revision — ${quotation ? `${quotation.quotation_number} · ${displayQuotationRevisionLabel(quotation.revision_number)}` : ''}`}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText="Create Revision"
      destroyOnClose
      width={640}
    >
      {quotation ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message={`${quotation.quotation_number} · ${displayQuotationRevisionLabel(quotation.revision_number)} → next draft revision`}
            description="A new Draft revision keeps the same quotation number. Prior customer-facing rows stay unchanged until supersession rules apply."
          />
          <Form form={form} layout="vertical">
            <Form.Item
              name="revision_type"
              label="Revision type"
              rules={[{ required: true, message: 'Select a revision type' }]}
              help={typeHelp}
            >
              <Select
                options={QUOTATION_REVISION_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="revision_reason"
              label="Reason"
              rules={[{ required: true, message: 'Reason is required' }]}
            >
              <TextArea rows={3} placeholder="Customer requested revised discount…" />
            </Form.Item>
            <Form.Item name="revision_notes" label="Notes (optional)">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item label="Estimate source">
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Current cost source: {currentEstLabel}
              </Text>
              {latestApproved && latestApproved.id !== estimationId ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={`Latest approved: ${latestApproved.estimation_number || 'EST'} · Rev ${latestApproved.revision_number ?? '—'}`}
                />
              ) : null}
              {estimateSource !== 'keep' ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Using a different Estimate revision changes the quotation cost source."
                  description="Quotation lines will not be automatically replaced. Review Draft lines before submission."
                />
              ) : null}
              <Radio.Group
                value={estimateSource}
                onChange={(e) => {
                  const v = e.target.value as EstimateSource;
                  setEstimateSource(v);
                  form.setFieldValue('estimate_source', v);
                }}
                disabled={!estimationId}
              >
                <Space direction="vertical">
                  <Radio value="keep">Keep current estimation</Radio>
                  <Radio value="latest_approved">Use latest approved estimation revision</Radio>
                  <Radio value="selected">Select estimation revision</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {estimateSource === 'selected' && estimationId ? (
              <Form.Item
                name="selected_estimation_id"
                label="Estimation revision"
                rules={[{ required: true, message: 'Select an estimation revision' }]}
              >
                <Select
                  loading={familyLoading}
                  options={estimationFamily.map((e) => ({
                    value: e.id,
                    label: `${e.estimation_number || 'EST'} · Rev ${e.revision_number ?? '—'} — ${e.status || '—'}${
                      e.is_latest_revision ? ' (latest)' : ''
                    }${e.id === estimationId ? ' — current' : ''}`,
                  }))}
                />
              </Form.Item>
            ) : null}
          </Form>
        </Space>
      ) : null}
    </Modal>
  );
};

export default CreateQuotationRevisionModal;
